import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Skeleton } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { useIsAdminUser } from "@/providers/auth/hooks";
import { EditFieldButton } from "@/shared/components/EditFieldButton";
import { CustomFieldRecord } from "@/shared/domain/customFields";

import {
  customFieldDisplayName,
  isCustomFieldEditable,
} from "./customFieldPresentation";
import styles from "./CustomFieldsTable.module.css";
import { CustomFieldStatusChip } from "./CustomFieldStatusChip";
import { CustomFieldTypeCell } from "./CustomFieldTypeCell";

const SKELETON_ROW_COUNT = 3;
// Per-column skeleton widths, varied so a loading table reads like real rows.
// The last entry is the actions column, sliced off when actions are hidden.
const SKELETON_CELL_WIDTHS = ["60%", "80%", "50%", "45%", "30%"];

interface CustomFieldsTableProps {
  fields: CustomFieldRecord[];
  isLoading?: boolean;
  // Always provided by the page; the table decides by admin status whether to
  // surface the actions that invoke these.
  onEditField: (field: CustomFieldRecord) => void;
  onDeleteField: (field: CustomFieldRecord) => void;
}

export function CustomFieldsTable({
  fields,
  isLoading = false,
  onEditField,
  onDeleteField,
}: CustomFieldsTableProps) {
  // Row actions are gated to admins, matching the page's New Field button.
  const isAdmin = useIsAdminUser();
  const columnCount = isAdmin ? 5 : 4;

  return (
    <table className={styles.table} aria-busy={isLoading}>
      <thead>
        <tr>
          <th className={styles.headerCell}>Display Name</th>
          <th className={styles.headerCell}>Field Name</th>
          <th className={styles.headerCell}>Type</th>
          <th className={styles.headerCell}>Status</th>
          {isAdmin ? (
            <th className={styles.headerCell} aria-label="Actions" />
          ) : null}
        </tr>
      </thead>
      <tbody>
        <CustomFieldsTableBody
          fields={fields}
          isLoading={isLoading}
          columnCount={columnCount}
          isAdmin={isAdmin}
          onEditField={onEditField}
          onDeleteField={onDeleteField}
        />
      </tbody>
    </table>
  );
}

interface CustomFieldsTableBodyProps {
  fields: CustomFieldRecord[];
  isLoading: boolean;
  columnCount: number;
  isAdmin: boolean;
  onEditField: (field: CustomFieldRecord) => void;
  onDeleteField: (field: CustomFieldRecord) => void;
}

function CustomFieldsTableBody({
  fields,
  isLoading,
  columnCount,
  isAdmin,
  onEditField,
  onDeleteField,
}: CustomFieldsTableBodyProps): React.ReactNode {
  if (isLoading) {
    return Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIdx) => (
      <tr key={rowIdx} className={styles.row}>
        {SKELETON_CELL_WIDTHS.slice(0, columnCount).map((width, colIdx) => (
          <td key={colIdx} className={styles.cell}>
            <Skeleton variant="text" width={width} animation="wave" />
          </td>
        ))}
      </tr>
    ));
  }

  if (fields.length === 0) {
    // Borderless centered message under the header (no `.row` class → no rule).
    return (
      <tr>
        <td colSpan={columnCount} className={styles.stateCell}>
          No custom fields defined.
        </td>
      </tr>
    );
  }

  return fields.map((field) => (
    <tr key={field.field_id} className={styles.row}>
      <td className={styles.cell}>
        <div className={styles.displayName}>
          {customFieldDisplayName(field)}
        </div>
        {field.description ? (
          <div className={styles.description}>{field.description}</div>
        ) : null}
      </td>
      <td className={classNames(styles.cell, styles.fieldNameCell)}>
        {field.field_name}
      </td>
      <td className={styles.cell}>
        <CustomFieldTypeCell type={field.field_type} />
      </td>
      <td className={classNames(styles.cell, styles.statusCell)}>
        <CustomFieldStatusChip status={field.status} />
      </td>
      {isAdmin ? (
        <td className={classNames(styles.cell, styles.actionsCell)}>
          <EditFieldButton
            editable={false}
            showEditableIcon
            disabled={!isCustomFieldEditable(field.status)}
            onClick={() => onEditField(field)}
          />
          <IconButton
            aria-label={`Delete ${customFieldDisplayName(field)}`}
            size="small"
            onClick={() => onDeleteField(field)}
          >
            <DeleteIcon color="error" fontSize="small" />
          </IconButton>
        </td>
      ) : null}
    </tr>
  ));
}
